import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { resetPassword, verifyResetToken, selectAuthLoading, selectAuthError, clearError } from '../store/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import './Auth.css';

// Global cache to prevent multiple verifications of the same token across component remounts
const tokenVerificationCache = new Map<string, { isValid: boolean; email?: string; promise?: Promise<any> }>();

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const initializationRef = useRef(false);

  const token = searchParams.get('token');

  const verifyTokenOnce = useCallback(async (tokenValue: string) => {
    // Check cache first
    if (tokenVerificationCache.has(tokenValue)) {
      const cached = tokenVerificationCache.get(tokenValue)!;
      if (cached.promise) {
        // Wait for ongoing verification
        return await cached.promise;
      } else {
        // Use cached result
        return cached;
      }
    }

    // Start verification and cache the promise
    const verificationPromise = (async () => {
      try {
        dispatch(clearError());
        const result = await dispatch(verifyResetToken({ token: tokenValue }));

        if (verifyResetToken.fulfilled.match(result)) {
          const verificationResult = { isValid: true, email: result.payload.email };
          tokenVerificationCache.set(tokenValue, verificationResult);
          return verificationResult;
        } else {
          const verificationResult = { isValid: false };
          tokenVerificationCache.set(tokenValue, verificationResult);
          return verificationResult;
        }
      } catch (error) {
        const verificationResult = { isValid: false };
        tokenVerificationCache.set(tokenValue, verificationResult);
        return verificationResult;
      }
    })();

    tokenVerificationCache.set(tokenValue, { isValid: false, promise: verificationPromise });
    return await verificationPromise;
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      setIsTokenValid(false);
      return;
    }

    if (initializationRef.current) {
      return;
    }

    initializationRef.current = true;

    // Verify token
    verifyTokenOnce(token).then((result) => {
      setIsTokenValid(result.isValid);
      if (result.email) {
        setUserEmail(result.email);
      }
    });
  }, [token, verifyTokenOnce]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    if (password.length < 8) {
      return;
    }

    try {
      dispatch(clearError());
      const result = await dispatch(resetPassword({ token, new_password: password }));

      if (resetPassword.fulfilled.match(result)) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Password reset successfully! Please sign in with your new password.'
            }
          });
        }, 3000);
      }
    } catch (error) {
      // Error is handled by the async thunk and stored in state
      console.error('Password reset failed:', error);
    }
  };

  // Loading state while verifying token
  if (isTokenValid === null) {
    return (
      <div
        className="auth-container"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/background.jpg)`
        }}
      >
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <LoadingSpinner size="lg" />
            <h1 style={{ marginTop: '20px' }}>Verifying Reset Link</h1>
            <p>Please wait while we verify your password reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!isTokenValid) {
    return (
      <div
        className="auth-container"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/background.jpg)`
        }}
      >
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
            <h1>Invalid Reset Link</h1>
            <p>This password reset link is invalid or has expired.</p>

            <div className="alert alert-error" style={{ margin: '20px 0' }}>
              <p>Reset links expire after 24 hours for security reasons.</p>
            </div>

            <div style={{ marginTop: '30px' }}>
              <Link to="/forgot-password" className="btn btn-primary btn-full" style={{ marginBottom: '10px' }}>
                Request New Reset Link
              </Link>
              <Link to="/login" className="btn btn-secondary btn-full">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div
        className="auth-container"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/background.jpg)`
        }}
      >
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h1>Password Reset Successfully!</h1>
            <p>Your password has been updated. You can now sign in with your new password.</p>

            <div className="alert alert-success" style={{ margin: '20px 0' }}>
              <p>Redirecting you to the sign in page...</p>
            </div>

            <div style={{ marginTop: '30px' }}>
              <LoadingSpinner size="sm" />
              <p style={{ marginTop: '10px' }}>
                <Link to="/login" style={{ color: '#007bff' }}>
                  Click here if you're not redirected automatically
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password form errors
  const passwordMismatch = password && confirmPassword && password !== confirmPassword;
  const passwordTooShort = password && password.length < 8;

  return (
    <div
      className="auth-container"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/background.jpg)`
      }}
    >
      <div className="auth-card">
        <h1>Set New Password</h1>
        <p>
          Create a new password for <strong>{userEmail}</strong>
        </p>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              minLength={8}
              placeholder="Enter your new password"
            />
            {passwordTooShort && (
              <small style={{ color: '#dc3545' }}>
                Password must be at least 8 characters long
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              required
              placeholder="Confirm your new password"
            />
            {passwordMismatch && (
              <small style={{ color: '#dc3545' }}>
                Passwords do not match
              </small>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={
              isLoading ||
              !password ||
              !confirmPassword ||
              !!passwordMismatch ||
              !!passwordTooShort
            }
          >
            {isLoading ? <LoadingSpinner size="sm" color="white" /> : 'Update Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Remember your password? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;